import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, styled } from "@mui/material";
import {
  usePublicTestSuiteResultQuery,
  usePublicTestCaseResultsFromTestSuiteQuery,
  usePublicErrorCodesQuery,
  usePublicStatusPatternQuery,
} from "../../useQuery/usePublicQuery";
import PrettyDonut from "../Reports/PrettyDonut";
import ErrorCodeDonut from "../Reports/ErrorCodesDonut";
import { PatternBarGraph, PatternDetailsTable } from "../Reports/PatternStatusComponents";
import dayjs from "dayjs";

const StyledTableCell = styled(Box)`
  color: #e0e0e0;
  font-size: 14px;
  font-weight: 600;
  padding: 16px 20px;
  background: #2550d1;
  border-bottom: 1px solid #e2e6f3;
`;

const StyledInfoBox = styled(Box)`
  padding: 16px 20px;
`;

const StyledTypography = styled(Typography)`
  font-size: 14px;
  font-weight: 600;
  color: #232d3d;
`;

const StyledColumnTitle = styled(StyledTableCell)`
  text-align: center;
  padding: 12px 10px;
  flex: 1;
  &:not(:last-child) {
    border-right: 2px solid rgba(226, 230, 243, 0.8);
  }
`;

const StyledColumnValue = styled(Box)`
  text-align: center;
  padding: 12px 10px;
  flex: 1;
  color: #2b3539;
  font-size: 16px;
  font-weight: 500;
  &:not(:last-child) {
    border-right: 2px solid rgba(226, 230, 243, 0.8);
  }
`;

const PublicsuiteResults = () => {
    const { pcid, tsid, tsrid, token, companyId } = useParams();
    const [testSuiteData, setTestSuiteData] = useState(null);

    const { data: testSuiteResults, isLoading } = usePublicTestSuiteResultQuery(tsid, tsrid, companyId, token,  {
        enabled: !!tsid && !!companyId && !!token,
    });


    const { data: testCaseData } = usePublicTestCaseResultsFromTestSuiteQuery(tsrid, "All", companyId, token, {
        enabled: !!tsrid && !!companyId && !!token,
    });

    const { data: errorCodesData } = usePublicErrorCodesQuery(tsrid, companyId, token, {
        enabled: !!tsrid && !!companyId && !!token,
    });


    const { data: patternData } = usePublicStatusPatternQuery(tsrid, companyId, token, {
        enabled: !!tsrid && !!companyId && !!token,
    });

    useEffect(() => {
        if (testSuiteResults?.length > 0) {
            const latestResult = testSuiteResults[0];
            setTestSuiteData(latestResult);
        }
    }, [testSuiteResults]);

    if (isLoading) {
        return (
            <Box p={3}>
                <Typography>Loading...</Typography>
            </Box>
        );
    }

    if (!testSuiteData) {
        return (
            <Box p={3}>
                <Typography>No test suite results found</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, maxWidth: "1400px", margin: "0 auto" }}>
            {/* Execution Summary */}
            <Box display="flex" gap={2}>
                <Box
                    mt={2}
                    flex={1}
                    bgcolor={"#fff"}
                    sx={{ borderTopRightRadius: "8px", borderTopLeftRadius: "8px" }}
                >
                    <Box display="flex" borderBottom={`2px solid rgba(226, 230, 243, 0.80)`}>
                        <StyledInfoBox flex={1} borderRight={`2px solid rgba(226, 230, 243, 0.80)`}>
                            <StyledTypography>Suite Name</StyledTypography>
                        </StyledInfoBox>
                        <StyledInfoBox flex={2}>
                            <StyledTypography>{testSuiteData.testsuitename}</StyledTypography>
                        </StyledInfoBox>
                    </Box>
                    <Box display="flex" borderBottom={`2px solid rgba(226, 230, 243, 0.80)`}>
                        <StyledInfoBox flex={1} borderRight={`2px solid rgba(226, 230, 243, 0.80)`}>
                            <StyledTypography>Session Start Time</StyledTypography>
                        </StyledInfoBox>
                        <StyledInfoBox flex={2}>
                            <StyledTypography>
                                {dayjs(testSuiteData.Executed_Date).format("DD/MM/YYYY HH:mm:ss")}
                            </StyledTypography>
                        </StyledInfoBox>
                    </Box>
                    <Box display="flex" borderBottom={`2px solid rgba(226, 230, 243, 0.80)`}>
                        <StyledInfoBox flex={1} borderRight={`2px solid rgba(226, 230, 243, 0.80)`}>
                            <StyledTypography>Execution Time</StyledTypography>
                        </StyledInfoBox>
                        <StyledInfoBox flex={2}>
                            <StyledTypography>{testSuiteData.Duration}</StyledTypography>
                        </StyledInfoBox>
                    </Box>
                </Box>
                <Box
                    mt={2}
                    flex={1}
                    bgcolor={"#fff"}
                    sx={{ borderTopRightRadius: "8px", borderTopLeftRadius: "8px" }}
                >
                    <Box display="flex" borderBottom={`2px solid rgba(226, 230, 243, 0.80)`}>
                        <StyledInfoBox flex={1} borderRight={`2px solid rgba(226, 230, 243, 0.80)`}>
                            <StyledTypography>No. of Test Cases</StyledTypography>
                        </StyledInfoBox>
                        <StyledInfoBox flex={2}>
                            <StyledTypography>{testSuiteData.totalCount}</StyledTypography>
                        </StyledInfoBox>
                    </Box>
                    <Box display="flex" borderBottom={`2px solid rgba(226, 230, 243, 0.80)`}>
                        <StyledInfoBox flex={1} borderRight={`2px solid rgba(226, 230, 243, 0.80)`}>
                            <StyledTypography>Session End Time</StyledTypography>
                        </StyledInfoBox>
                        <StyledInfoBox flex={2}>
                            <StyledTypography>
                                {dayjs(testSuiteData.Executed_Date)
                                    .add(testSuiteData.Duration, "second")
                                    .format("DD/MM/YYYY HH:mm:ss")}
                            </StyledTypography>
                        </StyledInfoBox>
                    </Box>
                    <Box display="flex" borderBottom={`2px solid rgba(226, 230, 243, 0.80)`}>
                        <StyledInfoBox flex={1} borderRight={`2px solid rgba(226, 230, 243, 0.80)`}>
                            <StyledTypography>Execution Status</StyledTypography>
                        </StyledInfoBox>
                        <StyledInfoBox flex={2}>
                            <StyledTypography>
                                {testSuiteData.Status === "STOPP" ? "STOPPED" : testSuiteData.Status}
                            </StyledTypography>
                        </StyledInfoBox>
                    </Box>
                </Box>
            </Box>

    
            <Box display="flex" mt={4} gap={2}>
                <Box flex={1}>
                    <PrettyDonut data={testSuiteData} />
                </Box>

           
                <Box flex={1}>
                    {errorCodesData && <ErrorCodeDonut data={errorCodesData} />}
                </Box>

            
                <Box flex={2}>
                    {patternData && <PatternBarGraph patterns={patternData.patterns} />}
                </Box>
            </Box>

  
            <Box mt={4}>
                <Box bgcolor={"#2550D1"} sx={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}>
                    <Typography
                        color="#E0E0E0"
                        fontWeight={600}
                        padding="16px 20px"
                        borderBottom={1}
                        borderColor={"gray"}
                    >
                        Test Case Summary
                    </Typography>
                </Box>

                <Box display="flex" bgcolor={"#2550D1"}>
                    <StyledColumnTitle>Total Test Cases</StyledColumnTitle>
                    <StyledColumnTitle>Pass</StyledColumnTitle>
                    <StyledColumnTitle>Fail</StyledColumnTitle>
                    <StyledColumnTitle>Not Executed</StyledColumnTitle>
                    <StyledColumnTitle>Skipped Over</StyledColumnTitle>
                    <StyledColumnTitle sx={{ backgroundColor: "#2E7D32" }}>
                        First Run Pass
                    </StyledColumnTitle>
                    <StyledColumnTitle sx={{ backgroundColor: "#2E7D32" }}>
                        First Run Fail
                    </StyledColumnTitle>
                    <StyledColumnTitle sx={{ backgroundColor: "#2E7D32" }}>
                        Second Run Pass
                    </StyledColumnTitle>
                    <StyledColumnTitle sx={{ backgroundColor: "#2E7D32" }}>
                        Second Run Fail
                    </StyledColumnTitle>
                </Box>


                <Box display="flex" bgcolor={"#ffffff"}>
                    <StyledColumnValue>{testSuiteData.totalCount}</StyledColumnValue>
                    <StyledColumnValue>{testSuiteData.passCount}</StyledColumnValue>
                    <StyledColumnValue>{testSuiteData.failCount}</StyledColumnValue>
                    <StyledColumnValue>
                        {testSuiteData.totalCount - (testSuiteData.passCount + testSuiteData.failCount)}
                    </StyledColumnValue>
                    <StyledColumnValue>{testSuiteData?.skippedOver || "0"}</StyledColumnValue>
                    <StyledColumnValue>{testSuiteData?.firstRunPassCount || "0"}</StyledColumnValue>
                    <StyledColumnValue>{testSuiteData?.firstRunFailCount || "0"}</StyledColumnValue>
                    <StyledColumnValue>{testSuiteData?.secondRunPassCount || "0"}</StyledColumnValue>
                    <StyledColumnValue>{testSuiteData?.secondRunFailCount || "0"}</StyledColumnValue>
                </Box>
            </Box>


            {patternData && (
                <Box mt={4}>
                    <PatternDetailsTable patterns={patternData.patterns} />
                </Box>
            )}
        </Box>
    );
}

export default PublicsuiteResults;